import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import apiClient from '@/lib/api/client';
import { SketchEngine3D, HelpContent } from '@/components/common/SketchEngine3D';
import type { EngineLayer, SketchData } from '@/components/common/SketchEngine3D';
import { planTo3d, DEFAULT_WALL_HEIGHT_IN } from '@/lib/drafting/planTo3d';

// ── 2D Drafting Suite (v1) ──────────────────────────────────────────────────
// Draw residential building plans in-app: snap-to-grid walls (2x4 / 2x6 /
// foundation thickness), doors & windows with schedule tags, room labels,
// ft-in dimension lines, multiple levels. Plans save to the server, export as
// scaled PNG, print to PDF, and attach straight into a job's documents.
// All geometry is world-space INCHES; the SVG viewBox handles zoom/pan.
//
// CR #208: 3D mode — the drawn plan extrudes into a building model
// (lib/drafting/planTo3d) rendered in SketchEngine3D, the full sketch toolkit
// extracted from the mmdl truss viewer: lumber/plywood, mitre/bevel cuts,
// autocut vs pieces AND the extruded walls/roof, layout/lay-run/generators,
// glide/magnet, measure, precision panel, keyboard layer, undo. The 3D
// sketch persists in drafting_plans.sketch_data, independent of the plan
// geometry — redraw a wall in 2D and the model re-extrudes around your
// sketch pieces. Wall height + massing roof live per level in the plan JSON.

interface Wall { id: number; x1: number; y1: number; x2: number; y2: number; thick: number }
interface Opening { id: number; wallId: number; offset: number; width: number; kind: 'door' | 'window' | 'opening'; tag: string }
interface Label { id: number; x: number; y: number; text: string; size: number }
interface Dim { id: number; x1: number; y1: number; x2: number; y2: number }
// Site-survey elements (#95): property lines / fences / setbacks as polylines,
// trees as canopy circles, pre-existing structures as hatched footprints.
interface SitePath { id: number; kind: 'property' | 'fence' | 'setback'; pts: { x: number; y: number }[] }
interface Tree { id: number; x: number; y: number; canopy: number }
interface Structure { id: number; x: number; y: number; w: number; h: number; label: string }
interface RoofSetting { kind: 'none' | 'gable' | 'flat'; pitch: number } // pitch = rise per 12
interface Level {
  name: string; walls: Wall[]; openings: Opening[]; labels: Label[]; dims: Dim[];
  sitePaths: SitePath[]; trees: Tree[]; structures: Structure[];
  heightIn?: number;   // 3D: wall height in inches (default 8'0")
  roof?: RoofSetting;  // 3D: massing roof (default none)
}
// PDF import (CR #209): the rendered plan page as a tracing underlay, plus the
// AI-extraction metadata driving the review banner. Both live in the plan JSON.
interface Underlay { url: string; opacity: number; visible: boolean; x: number; y: number; w: number; h: number }
interface ImportRef { from: { x: number; y: number }; to: { x: number; y: number }; labelled_in: number; text?: string; description: string }
interface ImportMeta { source: string; page: number; ref: ImportRef | null; confidence: string; notes: string[]; dismissed?: boolean }
interface PlanData { levels: Level[]; grid: number; underlay?: Underlay; importMeta?: ImportMeta }

const LAYERS_3D: EngineLayer[] = [
  { key: 'Wall', label: 'Walls', swatch: '#d3d3d3' },
  { key: 'Floor', label: 'Floors', swatch: '#decdaf' },
  { key: 'Roof', label: 'Roof', swatch: '#b07449' },
  { key: 'Site', label: 'Site', swatch: '#9a9a9a', catchAll: true },
];

interface PlanRow { id: number; name: string; job_id?: number | null; job_title?: string | null; updated_at: string; created_by_name?: string }
interface JobOpt { id: number; title: string }

type Tool = 'select' | 'wall' | 'room' | 'door' | 'window' | 'label' | 'dim'
  | 'property' | 'fence' | 'tree' | 'structure' | 'erase';

const THICKNESSES = [
  { label: '2x4 wall (3½")', value: 3.5 },
  { label: '2x6 wall (5½")', value: 5.5 },
  { label: 'Foundation (8")', value: 8 },
];

const SITE_STYLE: Record<SitePath['kind'], { stroke: string; dash: string; label: string }> = {
  property: { stroke: '#8B5A2B', dash: '24 8 4 8', label: 'Property line' }, // heavy dash-dot
  fence: { stroke: '#5C7F4F', dash: '14 8', label: 'Fence' },
  setback: { stroke: '#9aa0a6', dash: '4 8', label: 'Setback' },
};

const blankLevel = (name: string): Level =>
  ({ name, walls: [], openings: [], labels: [], dims: [], sitePaths: [], trees: [], structures: [] });
// Older saved plans predate the site arrays — normalize so .map never explodes.
const normLevel = (l: Partial<Level> & { name: string }): Level => ({
  name: l.name, walls: l.walls ?? [], openings: l.openings ?? [], labels: l.labels ?? [],
  dims: l.dims ?? [], sitePaths: l.sitePaths ?? [], trees: l.trees ?? [], structures: l.structures ?? [],
  heightIn: l.heightIn, roof: l.roof,
});
const blankPlan = (): PlanData => ({ levels: [blankLevel('Main floor')], grid: 6 });

// Single-key tool switching (#119) — muscle memory from CAD packages.
const TOOL_KEYS: Record<string, Tool> = {
  v: 'select', w: 'wall', r: 'room', d: 'door', n: 'window', t: 'label',
  m: 'dim', p: 'property', f: 'fence', g: 'tree', s: 'structure', e: 'erase',
};
const TOOL_HOTKEY: Record<string, string> =
  Object.fromEntries(Object.entries(TOOL_KEYS).map(([k, v]) => [v, k]));

const ftIn = (inches: number): string => {
  const neg = inches < 0 ? '-' : '';
  const v = Math.abs(inches);
  const ft = Math.floor(v / 12);
  const rem = Math.round((v - ft * 12) * 2) / 2; // nearest ½"
  if (!ft) return `${neg}${rem}"`;
  return rem ? `${neg}${ft}'-${rem}"` : `${neg}${ft}'`;
};

let idSeq = 1;
const nid = () => idSeq++;

// ── Import from PDF (CR #209) ───────────────────────────────────────────────
// Pick a PDF from the uploaded documents (or upload one) → the server renders
// the page, the AI drafts walls/openings/room names as an EDITABLE plan, and
// the page image comes along as a tracing underlay for correction.
interface DocRow { id: number; file_name: string; file_type?: string; job_title?: string | null }

function ImportPdfModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [docId, setDocId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/documents/all').then(r => {
      const all = (r.data.data ?? []) as DocRow[];
      setDocs(all.filter(d => /\.pdf$/i.test(d.file_name) || String(d.file_type ?? '').includes('pdf')));
    }).catch(() => {});
  }, []);

  const run = async () => {
    if (!file && !docId) { setError('Pick a PDF from the documents — or upload one'); return; }
    setBusy(true); setError('');
    try {
      let r;
      if (file) {
        const form = new FormData();
        form.append('file', file);
        form.append('page', String(page));
        r = await apiClient.post('/drafting/import-pdf', form,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 });
      } else {
        r = await apiClient.post('/drafting/import-pdf', { document_id: docId, page }, { timeout: 600000 });
      }
      navigate(`/drafting/${r.data.data.id}`);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error || err.message || 'Import failed');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-4 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">picture_as_pdf</span>
          <h2 className="font-bold text-gray-900">Import a builder's plan from PDF</h2>
        </div>
        <p className="text-xs text-gray-500">
          The AI reads one page and drafts the walls, openings and room names as an <b>editable 2D plan</b>,
          with the original page underneath as a tracing underlay. Treat it as a first draft — check the
          scale and fix anything it misread, then use the 3D tab to extrude it.
        </p>
        {busy ? (
          <div className="py-6 text-center space-y-2">
            <span className="material-symbols-outlined text-primary text-3xl animate-spin inline-block">progress_activity</span>
            <p className="text-sm font-semibold text-gray-700">Reading the plan… this can take 3–8 minutes on a dense sheet</p>
            <p className="text-xs text-gray-400">Rendering page {page}, extracting walls and dimensions</p>
          </div>
        ) : (
          <>
            <label className="block text-xs font-semibold text-gray-600">
              PDF from documents
              <select value={docId} disabled={!!file}
                onChange={e => setDocId(e.target.value ? Number(e.target.value) : '')}
                className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white disabled:opacity-50">
                <option value="">— choose a PDF —</option>
                {docs.map(d => (
                  <option key={d.id} value={d.id}>{d.job_title ? `${d.job_title} · ` : ''}{d.file_name}</option>
                ))}
              </select>
            </label>
            <label className="block text-xs font-semibold text-gray-600">
              …or upload a PDF
              <input type="file" accept="application/pdf,.pdf"
                onChange={e => { setFile(e.target.files?.[0] ?? null); if (e.target.files?.[0]) setDocId(''); }}
                className="mt-1 w-full text-xs" />
            </label>
            <label className="block text-xs font-semibold text-gray-600">
              Page (multi-page sets: the floor-plan page, 1–3)
              <input type="number" min={1} max={3} value={page}
                onChange={e => setPage(Math.max(1, Math.min(3, Number(e.target.value) || 1)))}
                className="mt-1 w-20 border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
            </label>
            {error && <p className="text-xs font-semibold text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="h-9 px-3 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={run} className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90">
                Import
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Plans list ──
function PlanList() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const load = () => apiClient.get('/drafting').then(r => { setPlans(r.data.data ?? []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = window.prompt('Name the plan (e.g. "Wilson dream home — main floor")');
    if (!name?.trim()) return;
    const r = await apiClient.post('/drafting', { name: name.trim(), data: blankPlan() });
    navigate(`/drafting/${r.data.data.id}`);
  };

  return (
    <MainLayout>
      <LargeTitleHeader
        title="Drafting"
        subtitle="Draw building plans — walls, openings, dimensions — and attach them to jobs"
        actions={
          <span className="flex items-center gap-2">
            <button onClick={() => setImporting(true)}
              title="AI-drafts an editable plan from a builder's-plan PDF page (review required)"
              className="flex items-center gap-1 rounded-lg h-9 px-3 border border-primary/40 text-primary text-sm font-bold hover:bg-primary/5">
              <span className="material-symbols-outlined text-base">picture_as_pdf</span> Import from PDF…
            </button>
            <button onClick={create} className="flex items-center gap-1 rounded-lg h-9 px-3 bg-primary text-white text-sm font-bold hover:bg-primary/90">
              <span className="material-symbols-outlined text-base">add</span> New plan
            </button>
          </span>
        }
      />
      {importing && <ImportPdfModal onClose={() => setImporting(false)} />}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
        ) : plans.length === 0 ? (
          <div className="p-12 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl block mb-3">architecture</span>
            <p className="text-sm text-gray-500">No plans yet — draw your first one.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {plans.map(p => (
              <button key={p.id} onClick={() => navigate(`/drafting/${p.id}`)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50">
                <span className="material-symbols-outlined text-primary">architecture</span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-gray-900 truncate">{p.name}</span>
                  <span className="block text-xs text-gray-400">
                    {p.job_title ? `${p.job_title} · ` : ''}updated {new Date(p.updated_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                    {p.created_by_name ? ` · ${p.created_by_name}` : ''}
                  </span>
                </span>
                <span className="material-symbols-outlined text-gray-300">chevron_right</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}

// ── Editor ──
function PlanEditor({ planId }: { planId: number }) {
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);
  const [name, setName] = useState('');
  const [jobId, setJobId] = useState<number | ''>('');
  const [jobs, setJobs] = useState<JobOpt[]>([]);
  const [plan, setPlan] = useState<PlanData>(blankPlan);
  const [levelIdx, setLevelIdx] = useState(0);
  const [tool, setTool] = useState<Tool>('wall');
  const [thick, setThick] = useState(5.5);
  const [dirty, setDirty] = useState(false);
  // Undo/redo history (#119) — refs hold the stacks, state drives button enable.
  const undoStack = useRef<PlanData[]>([]);
  const redoStack = useRef<PlanData[]>([]);
  const [history, setHistory] = useState({ canUndo: false, canRedo: false });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // viewport: world inches; view = {x, y, w} (h derived from element aspect)
  const [view, setView] = useState({ x: -60, y: -60, w: 720 });
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(null);
  const [polyPts, setPolyPts] = useState<{ x: number; y: number }[]>([]);
  const [roomSize, setRoomSize] = useState<{ w: number; h: number } | null>(null); // typed preset, e.g. 12x16
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const [selected, setSelected] = useState<{ kind: 'wall' | 'opening' | 'label' | 'dim' | 'sitepath' | 'tree' | 'structure'; id: number } | null>(null);
  const [dragging, setDragging] = useState<{ kind: 'end1' | 'end2' | 'label' | 'tree'; id: number } | null>(null);
  const panRef = useRef<{ sx: number; sy: number; vx: number; vy: number } | null>(null);
  // ── 3D mode (#208) ──
  const [viewMode, setViewMode] = useState<'2d' | '3d'>('2d');
  const [help3d, setHelp3d] = useState(false);
  // Synced during RENDER (not an effect): the engine's scene effect is a
  // child effect and fires before this component's effects would — an
  // effect-based sync handed loadModel a one-update-stale plan.
  const planStateRef = useRef(plan);
  planStateRef.current = plan;
  // The engine rebuilds the scene when this fingerprint changes — i.e. when
  // anything that extrudes (walls, openings, site, heights, roof) changes.
  const modelKey = useMemo(() => JSON.stringify(plan.levels.map(l =>
    [l.walls, l.openings, l.structures, l.trees, l.heightIn ?? DEFAULT_WALL_HEIGHT_IN, l.roof ?? null])), [plan]);
  const loadModel = useCallback(async () => planTo3d(planStateRef.current.levels), []);
  const sketchAdapter = useMemo(() => ({
    load: async () => (await apiClient.get(`/drafting/${planId}/sketch`)).data.data as unknown,
    save: async (data: SketchData) => { await apiClient.put(`/drafting/${planId}/sketch`, { data }); },
  }), [planId]);
  const setLevelProp = (i: number, patch: Partial<Level>) => {
    setPlan(p => ({ ...p, levels: p.levels.map((l, j) => j === i ? { ...l, ...patch } : l) }));
    setDirty(true);
  };

  const level = plan.levels[levelIdx] ?? plan.levels[0];

  // load plan + jobs
  useEffect(() => {
    apiClient.get(`/drafting/${planId}`).then(r => {
      const row = r.data.data;
      setName(row.name);
      setJobId(row.job_id || '');
      try {
        const d = JSON.parse(row.data) as PlanData;
        if (d?.levels?.length) {
          const norm: PlanData = { ...d, levels: d.levels.map(normLevel) };
          setPlan(norm);
          // Imported plans (#209) land wherever the page's scale put them —
          // fit the view to the underlay so the drawing is on screen.
          if (norm.underlay) {
            const u = norm.underlay;
            setView({ x: u.x - u.w * 0.05, y: u.y - u.h * 0.05, w: u.w * 1.1 });
          }
          // Fresh document — nothing to undo back into.
          undoStack.current = []; redoStack.current = [];
          setHistory({ canUndo: false, canRedo: false });
          // continue id sequence past anything loaded
          const ids = norm.levels.flatMap(l =>
            [...l.walls, ...l.openings, ...l.labels, ...l.dims, ...l.sitePaths, ...l.trees, ...l.structures].map(e => e.id));
          idSeq = Math.max(0, ...ids) + 1;
        }
      } catch { /* keep blank */ }
    }).catch(() => navigate('/drafting'));
    apiClient.get('/jobs?limit=200').then(r => {
      const d = r.data?.data ?? r.data;
      const list = (Array.isArray(d) ? d : (Array.isArray(d?.jobs) ? d.jobs : [])) as (JobOpt & { status?: string })[];
      setJobs(list.filter(j => !['completed', 'cancelled'].includes(String(j.status || '').toLowerCase())));
    }).catch(() => {});
  }, [planId, navigate]);

  // warn on close with unsaved work
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirty) { e.preventDefault(); } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // Keyboard (#95, expanded #119): Delete removes the selection, Escape cancels
  // the current draw, Enter finishes a polyline, Ctrl/Cmd+Z / Shift+Z undo-redo,
  // Ctrl/Cmd+D duplicates, and single letters switch tools (AutoCAD-style).
  // No deps — re-binds per render so the handlers never see stale state.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (viewMode !== '2d') return; // 3D mode: the sketch engine owns the keyboard
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); redo(); return; }
      if (mod && e.key.toLowerCase() === 'd') { e.preventDefault(); duplicateSelected(); return; }
      if (mod) return; // leave other browser shortcuts alone
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected) { e.preventDefault(); deleteSelected(); }
      if (e.key === 'Escape') { setDrawStart(null); setPolyPts([]); setSelected(null); }
      if (e.key === 'Enter') finishPoly();
      const hot = TOOL_KEYS[e.key.toLowerCase()];
      if (hot) { setTool(hot); setDrawStart(null); setPolyPts([]); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  // ── Undo / redo (#119) ────────────────────────────────────────────────────
  // Every mutation pushes the PREVIOUS plan onto the undo stack (capped at 50);
  // undo moves it to the redo stack. Loading a plan clears both.
  // NB: the stack push happens OUTSIDE the setPlan updater — updaters must stay
  // pure, and setState-inside-an-updater doesn't reliably flush (undo button
  // stayed disabled). `plan` is current here because mutate runs from handlers.
  const mutate = (fn: (l: Level) => Level) => {
    undoStack.current = [...undoStack.current.slice(-49), plan];
    redoStack.current = [];
    setHistory({ canUndo: true, canRedo: false });
    setPlan(p => ({ ...p, levels: p.levels.map((l, i) => i === levelIdx ? fn(l) : l) }));
    setDirty(true);
  };

  const undo = () => {
    const prev = undoStack.current.pop();
    if (!prev) return;
    redoStack.current = [...redoStack.current.slice(-49), plan];
    setPlan(prev);
    setHistory({ canUndo: undoStack.current.length > 0, canRedo: true });
    setDirty(true);
    setSelected(null);
  };

  const redo = () => {
    const next = redoStack.current.pop();
    if (!next) return;
    undoStack.current = [...undoStack.current.slice(-49), plan];
    setPlan(next);
    setHistory({ canUndo: true, canRedo: redoStack.current.length > 0 });
    setDirty(true);
    setSelected(null);
  };

  // Ctrl/Cmd+D — copy the selected element 12" down-right so it's grabbable.
  const duplicateSelected = () => {
    if (!selected) return;
    const OFF = 12;
    mutate(l => {
      if (selected.kind === 'wall') {
        const w = l.walls.find(x => x.id === selected.id);
        if (!w) return l;
        return { ...l, walls: [...l.walls, { ...w, id: idSeq++, x1: w.x1 + OFF, y1: w.y1 + OFF, x2: w.x2 + OFF, y2: w.y2 + OFF }] };
      }
      if (selected.kind === 'label') {
        const t = l.labels.find(x => x.id === selected.id);
        return t ? { ...l, labels: [...l.labels, { ...t, id: idSeq++, x: t.x + OFF, y: t.y + OFF }] } : l;
      }
      if (selected.kind === 'tree') {
        const t = l.trees.find(x => x.id === selected.id);
        return t ? { ...l, trees: [...l.trees, { ...t, id: idSeq++, x: t.x + OFF, y: t.y + OFF }] } : l;
      }
      if (selected.kind === 'structure') {
        const s = l.structures.find(x => x.id === selected.id);
        return s ? { ...l, structures: [...l.structures, { ...s, id: idSeq++, x: s.x + OFF, y: s.y + OFF }] } : l;
      }
      return l;
    });
  };

  // ── PDF-import review (#209): underlay controls + scale correction ────────
  const [scaleFix, setScaleFix] = useState('');
  const setUnderlay = (patch: Partial<Underlay>) => {
    setPlan(p => p.underlay ? { ...p, underlay: { ...p.underlay, ...patch } } : p);
    setDirty(true);
  };
  const dismissBanner = () => {
    setPlan(p => p.importMeta ? { ...p, importMeta: { ...p.importMeta, dismissed: true } } : p);
    setDirty(true);
  };
  // Rescale EVERYTHING (all levels + underlay + the reference itself) about the
  // origin by k — the one-input fix when the AI misread the reference dimension.
  const rescalePlan = (k: number) => {
    if (!Number.isFinite(k) || k <= 0) return;
    undoStack.current = [...undoStack.current.slice(-49), plan];
    redoStack.current = [];
    setHistory({ canUndo: true, canRedo: false });
    const sc = (v: number) => Math.round(v * k * 2) / 2; // keep ½" precision
    setPlan(p => ({
      ...p,
      levels: p.levels.map(l => ({
        ...l,
        walls: l.walls.map(w => ({ ...w, x1: sc(w.x1), y1: sc(w.y1), x2: sc(w.x2), y2: sc(w.y2) })),
        openings: l.openings.map(o => ({ ...o, offset: sc(o.offset), width: Math.max(12, sc(o.width)) })),
        labels: l.labels.map(x => ({ ...x, x: sc(x.x), y: sc(x.y) })),
        dims: l.dims.map(d => ({ ...d, x1: sc(d.x1), y1: sc(d.y1), x2: sc(d.x2), y2: sc(d.y2) })),
        sitePaths: l.sitePaths.map(s => ({ ...s, pts: s.pts.map(pt => ({ x: sc(pt.x), y: sc(pt.y) })) })),
        trees: l.trees.map(t => ({ ...t, x: sc(t.x), y: sc(t.y), canopy: sc(t.canopy) })),
        structures: l.structures.map(s => ({ ...s, x: sc(s.x), y: sc(s.y), w: sc(s.w), h: sc(s.h) })),
      })),
      underlay: p.underlay
        ? { ...p.underlay, x: p.underlay.x * k, y: p.underlay.y * k, w: p.underlay.w * k, h: p.underlay.h * k }
        : p.underlay,
      importMeta: p.importMeta?.ref
        ? {
            ...p.importMeta,
            ref: {
              ...p.importMeta.ref,
              from: { x: p.importMeta.ref.from.x * k, y: p.importMeta.ref.from.y * k },
              to: { x: p.importMeta.ref.to.x * k, y: p.importMeta.ref.to.y * k },
              labelled_in: p.importMeta.ref.labelled_in * k,
            },
          }
        : p.importMeta,
    }));
    setView(v => ({ x: v.x * k, y: v.y * k, w: v.w * k })); // keep the drawing on screen
    setDirty(true);
    setSelected(null);
    setMsg(`Rescaled ×${k.toFixed(3)}`);
    setTimeout(() => setMsg(''), 2500);
  };
  const applyScaleFix = () => {
    const ref = plan.importMeta?.ref;
    const ft = parseFloat(scaleFix);
    if (!ref || !(ft > 0)) return;
    rescalePlan((ft * 12) / ref.labelled_in);
    setScaleFix('');
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/drafting/${planId}`, { name, job_id: jobId || null, data: plan });
      setDirty(false);
      setMsg('Saved');
      setTimeout(() => setMsg(''), 1500);
    } catch { setMsg('Save failed'); }
    finally { setSaving(false); }
  };

  // ── coordinate helpers ──
  const toWorld = (e: { clientX: number; clientY: number }) => {
    const el = svgRef.current!;
    const r = el.getBoundingClientRect();
    const h = view.w * (r.height / r.width);
    return {
      x: view.x + ((e.clientX - r.left) / r.width) * view.w,
      y: view.y + ((e.clientY - r.top) / r.height) * h,
    };
  };
  const snap = (v: number) => Math.round(v / plan.grid) * plan.grid;

  const nextTag = (kind: 'door' | 'window') => {
    const prefix = kind === 'door' ? 'D' : 'W';
    const used = plan.levels.flatMap(l => l.openings).filter(o => o.tag.startsWith(prefix)).length;
    return `${prefix}${used + 1}`;
  };

  const wallAt = (pt: { x: number; y: number }, tolerance = 6): Wall | null => {
    let best: Wall | null = null, bestD = tolerance;
    for (const w of level.walls) {
      const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
      const len2 = dx * dx + dy * dy || 1;
      const t = Math.max(0, Math.min(1, ((pt.x - w.x1) * dx + (pt.y - w.y1) * dy) / len2));
      const px = w.x1 + t * dx, py = w.y1 + t * dy;
      const d = Math.hypot(pt.x - px, pt.y - py);
      if (d < bestD) { bestD = d; best = w; }
    }
    return best;
  };

  // ── pointer handling ──
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button === 1 || tool === 'select' && e.shiftKey) return;
    const pt = toWorld(e);
    const sp = { x: snap(pt.x), y: snap(pt.y) };

    if (tool === 'wall') {
      if (!drawStart) setDrawStart(sp);
      else {
        // ortho-snap: favor axis alignment unless clearly diagonal
        const end = orthoSnap(drawStart, sp);
        if (end.x !== drawStart.x || end.y !== drawStart.y) {
          mutate(l => ({ ...l, walls: [...l.walls, { id: nid(), x1: drawStart.x, y1: drawStart.y, x2: end.x, y2: end.y, thick }] }));
        }
        setDrawStart(end); // chain walls
      }
      return;
    }
    if (tool === 'room') {
      // Parametric wall set (#95): a preset W×D box placed in one click, or
      // corner→corner when no preset is active. Corner clicked = top-left.
      const commitBox = (a: { x: number; y: number }, b: { x: number; y: number }) => {
        const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x);
        const y1 = Math.min(a.y, b.y), y2 = Math.max(a.y, b.y);
        if (x2 - x1 < 12 || y2 - y1 < 12) return;
        mutate(l => ({
          ...l,
          walls: [
            ...l.walls,
            { id: nid(), x1, y1, x2, y2: y1, thick },
            { id: nid(), x1: x2, y1, x2, y2, thick },
            { id: nid(), x1: x2, y1: y2, x2: x1, y2, thick },
            { id: nid(), x1, y1: y2, x2: x1, y2: y1, thick },
          ],
        }));
      };
      if (roomSize) { commitBox(sp, { x: sp.x + roomSize.w, y: sp.y + roomSize.h }); return; }
      if (!drawStart) setDrawStart(sp);
      else { commitBox(drawStart, sp); setDrawStart(null); }
      return;
    }
    if (tool === 'property' || tool === 'fence') {
      // Polyline: click to add vertices, double-click (or Enter) to finish
      setPolyPts(pts => (pts.length && pts[pts.length - 1].x === sp.x && pts[pts.length - 1].y === sp.y) ? pts : [...pts, sp]);
      return;
    }
    if (tool === 'tree') {
      mutate(l => ({ ...l, trees: [...l.trees, { id: nid(), x: sp.x, y: sp.y, canopy: 120 }] }));
      return;
    }
    if (tool === 'structure') {
      if (!drawStart) setDrawStart(sp);
      else {
        const x = Math.min(drawStart.x, sp.x), y = Math.min(drawStart.y, sp.y);
        const w = Math.abs(sp.x - drawStart.x), h = Math.abs(sp.y - drawStart.y);
        if (w >= 12 && h >= 12) {
          mutate(l => ({ ...l, structures: [...l.structures, { id: nid(), x, y, w, h, label: 'Existing structure' }] }));
        }
        setDrawStart(null);
      }
      return;
    }
    if (tool === 'door' || tool === 'window') {
      const w = wallAt(pt, 10);
      if (!w) { setMsg('Tap on a wall to place the opening'); setTimeout(() => setMsg(''), 1500); return; }
      const dx = w.x2 - w.x1, dy = w.y2 - w.y1;
      const len = Math.hypot(dx, dy);
      const t = Math.max(0, Math.min(1, ((pt.x - w.x1) * dx + (pt.y - w.y1) * dy) / (len * len)));
      const width = tool === 'door' ? 32 : 48;
      const offset = Math.max(2, Math.min(len - width - 2, snap(t * len - width / 2)));
      mutate(l => ({ ...l, openings: [...l.openings, { id: nid(), wallId: w.id, offset, width, kind: tool, tag: nextTag(tool) }] }));
      return;
    }
    if (tool === 'label') {
      const text = window.prompt('Label text (room name, note…)');
      if (text?.trim()) mutate(l => ({ ...l, labels: [...l.labels, { id: nid(), x: sp.x, y: sp.y, text: text.trim(), size: 12 }] }));
      return;
    }
    if (tool === 'dim') {
      if (!drawStart) setDrawStart(sp);
      else {
        mutate(l => ({ ...l, dims: [...l.dims, { id: nid(), x1: drawStart.x, y1: drawStart.y, x2: sp.x, y2: sp.y }] }));
        setDrawStart(null);
      }
      return;
    }
    if (tool === 'erase') {
      eraseAt(pt);
      return;
    }
    // select
    hitSelect(pt);
  };

  const orthoSnap = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const dx = Math.abs(b.x - a.x), dy = Math.abs(b.y - a.y);
    if (dx >= dy * 2) return { x: b.x, y: a.y };
    if (dy >= dx * 2) return { x: a.x, y: b.y };
    // 45° — equalize
    const d = Math.max(dx, dy);
    return { x: a.x + Math.sign(b.x - a.x) * d, y: a.y + Math.sign(b.y - a.y) * d };
  };

  // Commit an in-progress property/fence polyline (double-click or Enter).
  const finishPoly = () => {
    if (polyPts.length >= 2 && (tool === 'property' || tool === 'fence')) {
      const kind = tool;
      const pts = polyPts;
      mutate(l => ({ ...l, sitePaths: [...l.sitePaths, { id: nid(), kind, pts }] }));
    }
    setPolyPts([]);
  };

  const distToSeg = (pt: { x: number; y: number }, a: { x: number; y: number }, b: { x: number; y: number }) => {
    const dx = b.x - a.x, dy = b.y - a.y;
    const len2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((pt.x - a.x) * dx + (pt.y - a.y) * dy) / len2));
    return Math.hypot(pt.x - (a.x + t * dx), pt.y - (a.y + t * dy));
  };

  const sitePathAt = (pt: { x: number; y: number }, tol = 8): SitePath | null => {
    for (const sp of level.sitePaths) {
      for (let i = 0; i < sp.pts.length - 1; i++) {
        if (distToSeg(pt, sp.pts[i], sp.pts[i + 1]) < tol) return sp;
      }
    }
    return null;
  };

  const hitSelect = (pt: { x: number; y: number }) => {
    for (const lb of level.labels) {
      if (Math.hypot(pt.x - lb.x, pt.y - lb.y) < 12) { setSelected({ kind: 'label', id: lb.id }); return; }
    }
    for (const o of level.openings) {
      const w = level.walls.find(x => x.id === o.wallId);
      if (!w) continue;
      const c = openingCenter(w, o);
      if (Math.hypot(pt.x - c.x, pt.y - c.y) < Math.max(10, o.width / 2)) { setSelected({ kind: 'opening', id: o.id }); return; }
    }
    const w = wallAt(pt, 8);
    if (w) { setSelected({ kind: 'wall', id: w.id }); return; }
    for (const d of level.dims) {
      const mid = { x: (d.x1 + d.x2) / 2, y: (d.y1 + d.y2) / 2 };
      if (Math.hypot(pt.x - mid.x, pt.y - mid.y) < 14) { setSelected({ kind: 'dim', id: d.id }); return; }
    }
    for (const t of level.trees) {
      if (Math.hypot(pt.x - t.x, pt.y - t.y) < Math.max(12, t.canopy / 2)) { setSelected({ kind: 'tree', id: t.id }); return; }
    }
    for (const s of level.structures) {
      if (pt.x >= s.x && pt.x <= s.x + s.w && pt.y >= s.y && pt.y <= s.y + s.h) { setSelected({ kind: 'structure', id: s.id }); return; }
    }
    const sp = sitePathAt(pt);
    if (sp) { setSelected({ kind: 'sitepath', id: sp.id }); return; }
    setSelected(null);
  };

  const eraseAt = (pt: { x: number; y: number }) => {
    for (const o of level.openings) {
      const w = level.walls.find(x => x.id === o.wallId);
      if (!w) continue;
      const c = openingCenter(w, o);
      if (Math.hypot(pt.x - c.x, pt.y - c.y) < Math.max(10, o.width / 2)) {
        mutate(l => ({ ...l, openings: l.openings.filter(x => x.id !== o.id) }));
        return;
      }
    }
    const w = wallAt(pt, 8);
    if (w) {
      mutate(l => ({
        ...l,
        walls: l.walls.filter(x => x.id !== w.id),
        openings: l.openings.filter(o => o.wallId !== w.id),
      }));
      return;
    }
    for (const lb of level.labels) {
      if (Math.hypot(pt.x - lb.x, pt.y - lb.y) < 12) { mutate(l => ({ ...l, labels: l.labels.filter(x => x.id !== lb.id) })); return; }
    }
    for (const d of level.dims) {
      const mid = { x: (d.x1 + d.x2) / 2, y: (d.y1 + d.y2) / 2 };
      if (Math.hypot(pt.x - mid.x, pt.y - mid.y) < 14) { mutate(l => ({ ...l, dims: l.dims.filter(x => x.id !== d.id) })); return; }
    }
    for (const t of level.trees) {
      if (Math.hypot(pt.x - t.x, pt.y - t.y) < Math.max(12, t.canopy / 2)) { mutate(l => ({ ...l, trees: l.trees.filter(x => x.id !== t.id) })); return; }
    }
    for (const s of level.structures) {
      if (pt.x >= s.x && pt.x <= s.x + s.w && pt.y >= s.y && pt.y <= s.y + s.h) { mutate(l => ({ ...l, structures: l.structures.filter(x => x.id !== s.id) })); return; }
    }
    const spth = sitePathAt(pt);
    if (spth) { mutate(l => ({ ...l, sitePaths: l.sitePaths.filter(x => x.id !== spth.id) })); return; }
  };

  // Delete-selected shared by the properties panel button and the Delete key.
  const deleteSelected = () => {
    if (!selected) return;
    mutate(l => ({
      ...l,
      walls: selected.kind === 'wall' ? l.walls.filter(w => w.id !== selected.id) : l.walls,
      openings: selected.kind === 'opening' ? l.openings.filter(o => o.id !== selected.id)
        : selected.kind === 'wall' ? l.openings.filter(o => o.wallId !== selected.id) : l.openings,
      labels: selected.kind === 'label' ? l.labels.filter(x => x.id !== selected.id) : l.labels,
      dims: selected.kind === 'dim' ? l.dims.filter(x => x.id !== selected.id) : l.dims,
      sitePaths: selected.kind === 'sitepath' ? l.sitePaths.filter(x => x.id !== selected.id) : l.sitePaths,
      trees: selected.kind === 'tree' ? l.trees.filter(x => x.id !== selected.id) : l.trees,
      structures: selected.kind === 'structure' ? l.structures.filter(x => x.id !== selected.id) : l.structures,
    }));
    setSelected(null);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const pt = toWorld(e);
    setCursor({ x: snap(pt.x), y: snap(pt.y) });
    if (panRef.current) {
      const el = svgRef.current!;
      const r = el.getBoundingClientRect();
      const scale = view.w / r.width;
      setView(v => ({ ...v, x: panRef.current!.vx - (e.clientX - panRef.current!.sx) * scale, y: panRef.current!.vy - (e.clientY - panRef.current!.sy) * scale }));
      return;
    }
    if (dragging) {
      const sp = { x: snap(pt.x), y: snap(pt.y) };
      if (dragging.kind === 'label') {
        mutate(l => ({ ...l, labels: l.labels.map(x => x.id === dragging.id ? { ...x, x: sp.x, y: sp.y } : x) }));
      } else if (dragging.kind === 'tree') {
        mutate(l => ({ ...l, trees: l.trees.map(x => x.id === dragging.id ? { ...x, x: sp.x, y: sp.y } : x) }));
      } else {
        mutate(l => ({
          ...l,
          walls: l.walls.map(x => x.id === dragging.id
            ? (dragging.kind === 'end1' ? { ...x, x1: sp.x, y1: sp.y } : { ...x, x2: sp.x, y2: sp.y })
            : x),
        }));
      }
    }
  };

  const onPointerUp = () => { setDragging(null); panRef.current = null; };

  const onWheel = (e: React.WheelEvent) => {
    const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
    const pt = toWorld(e);
    setView(v => {
      const w = Math.max(60, Math.min(4800, v.w * factor));
      return { x: pt.x - (pt.x - v.x) * (w / v.w), y: pt.y - (pt.y - v.y) * (w / v.w), w };
    });
  };

  const startPan = (e: React.PointerEvent) => {
    panRef.current = { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
  };

  const openingCenter = (w: Wall, o: Opening) => {
    const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1;
    const ux = (w.x2 - w.x1) / len, uy = (w.y2 - w.y1) / len;
    const t = o.offset + o.width / 2;
    return { x: w.x1 + ux * t, y: w.y1 + uy * t };
  };

  // ── export ──
  const exportPng = useCallback(async (): Promise<Blob | null> => {
    const el = svgRef.current;
    if (!el) return null;
    // bounds of geometry + margin
    const pts = level.walls.flatMap(w => [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }])
      .concat(level.labels, level.dims.flatMap(d => [{ x: d.x1, y: d.y1 }, { x: d.x2, y: d.y2 }]))
      .concat(level.sitePaths.flatMap(s => s.pts))
      .concat(level.trees.flatMap(t => [{ x: t.x - t.canopy / 2, y: t.y - t.canopy / 2 }, { x: t.x + t.canopy / 2, y: t.y + t.canopy / 2 }]))
      .concat(level.structures.flatMap(s => [{ x: s.x, y: s.y }, { x: s.x + s.w, y: s.y + s.h }]));
    if (!pts.length) return null;
    const minX = Math.min(...pts.map(p => p.x)) - 48, maxX = Math.max(...pts.map(p => p.x)) + 48;
    const minY = Math.min(...pts.map(p => p.y)) - 48, maxY = Math.max(...pts.map(p => p.y)) + 48;
    const clone = el.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('viewBox', `${minX} ${minY} ${maxX - minX} ${maxY - minY}`);
    const scale = 8; // 8 px per inch ≈ crisp at print
    clone.setAttribute('width', String((maxX - minX) * scale));
    clone.setAttribute('height', String((maxY - minY) * scale));
    clone.querySelector('#cursor-layer')?.remove();
    // The underlay is an external image — SVG-in-<img> can't fetch it, so it
    // would export as a blank box. Exports are the drawn plan only.
    clone.querySelector('#underlay-layer')?.remove();
    const xml = new XMLSerializer().serializeToString(clone);
    const img = new Image();
    const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml' }));
    return new Promise(resolve => {
      img.onload = () => {
        const cv = document.createElement('canvas');
        cv.width = (maxX - minX) * scale;
        cv.height = (maxY - minY) * scale;
        const ctx = cv.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, cv.width, cv.height);
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        cv.toBlob(b => resolve(b), 'image/png');
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  }, [level]);

  const downloadPng = async () => {
    const b = await exportPng();
    if (!b) { setMsg('Nothing to export yet'); setTimeout(() => setMsg(''), 1500); return; }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b);
    a.download = `${name || 'plan'}-${level.name}.png`.replace(/\s+/g, '-').toLowerCase();
    a.click();
  };

  const attachToJob = async () => {
    if (!jobId) { setMsg('Pick a job first'); setTimeout(() => setMsg(''), 1800); return; }
    const b = await exportPng();
    if (!b) { setMsg('Nothing to export yet'); setTimeout(() => setMsg(''), 1500); return; }
    const form = new FormData();
    form.append('file', new File([b], `${name || 'plan'}-${level.name}.png`.replace(/\s+/g, '-').toLowerCase(), { type: 'image/png' }));
    try {
      await apiClient.post(`/jobs/${jobId}/documents`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg('Attached to job documents ✓');
      setTimeout(() => setMsg(''), 2500);
    } catch { setMsg('Attach failed'); }
  };

  const printPdf = () => window.print();

  // ── rendering ──
  const el = svgRef.current;
  const aspect = el ? el.getBoundingClientRect().height / Math.max(1, el.getBoundingClientRect().width) : 0.6;
  const viewH = view.w * (aspect || 0.6);

  const gridLines = useMemo(() => {
    const out: { x?: number; y?: number; major: boolean }[] = [];
    const step = 12;
    const x0 = Math.floor(view.x / step) * step;
    const y0 = Math.floor(view.y / step) * step;
    for (let x = x0; x <= view.x + view.w; x += step) out.push({ x, major: x % 48 === 0 });
    for (let y = y0; y <= view.y + viewH; y += step) out.push({ y, major: y % 48 === 0 });
    return out;
  }, [view, viewH]);

  const selWall = selected?.kind === 'wall' ? level.walls.find(w => w.id === selected.id) : null;
  const selOpening = selected?.kind === 'opening' ? level.openings.find(o => o.id === selected.id) : null;
  const selLabel = selected?.kind === 'label' ? level.labels.find(l => l.id === selected.id) : null;
  const selSitePath = selected?.kind === 'sitepath' ? level.sitePaths.find(s => s.id === selected.id) : null;
  const selTree = selected?.kind === 'tree' ? level.trees.find(t => t.id === selected.id) : null;
  const selStructure = selected?.kind === 'structure' ? level.structures.find(s => s.id === selected.id) : null;

  const toolBtn = (t: Tool, icon: string, label: string) => (
    <button key={t} onClick={() => { setTool(t); setDrawStart(null); setPolyPts([]); }}
      title={`${label}${TOOL_HOTKEY[t] ? ` (${TOOL_HOTKEY[t].toUpperCase()})` : ''}`}
      className={`h-10 w-10 rounded-lg flex flex-col items-center justify-center ${tool === t ? 'bg-[#C77D3F] text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
      <span className="material-symbols-outlined text-lg leading-none">{icon}</span>
      <span className="text-[7px] font-bold leading-none mt-0.5">{label}</span>
    </button>
  );

  return (
    <MainLayout noPadding>
      <div className="h-full flex flex-col print:h-auto">
        {/* Top bar */}
        <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-200 bg-white flex-wrap print:hidden">
          <button onClick={() => { if (!dirty || window.confirm('Unsaved changes — leave anyway?')) navigate('/drafting'); }}
            className="h-9 w-9 rounded-lg text-gray-500 hover:bg-gray-100 flex items-center justify-center">
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <input value={name} onChange={e => { setName(e.target.value); setDirty(true); }}
            className="font-bold text-gray-900 border-b border-transparent focus:border-[#C77D3F] focus:outline-none bg-transparent min-w-[10rem]" />
          <select value={jobId} onChange={e => { setJobId(e.target.value ? Number(e.target.value) : ''); setDirty(true); }}
            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none max-w-[11rem]">
            <option value="">No job attached</option>
            {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
          {/* 2D / 3D mode (#208) */}
          <span className="flex rounded-lg overflow-hidden border border-gray-200">
            {(['2d', '3d'] as const).map(v => (
              <button key={v}
                onClick={() => { setViewMode(v); setDrawStart(null); setPolyPts([]); setSelected(null); }}
                title={v === '3d' ? 'Extrude the plan into a 3D model and sketch framing on it' : 'Back to the 2D plan editor'}
                className={`h-9 px-3 text-xs font-bold flex items-center gap-1 ${viewMode === v ? 'bg-[#2C1810] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                <span className="material-symbols-outlined text-base">{v === '3d' ? 'view_in_ar' : 'draw'}</span>
                {v.toUpperCase()}
              </button>
            ))}
          </span>
          {msg && <span className="text-xs font-semibold text-green-700">{msg}</span>}
          <span className="ml-auto flex items-center gap-1.5">
            <button onClick={downloadPng} title="Download PNG"
              className="h-9 px-2.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 flex items-center gap-1">
              <span className="material-symbols-outlined text-base">image</span> PNG
            </button>
            <button onClick={printPdf} title="Print / save as PDF"
              className="h-9 px-2.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 flex items-center gap-1">
              <span className="material-symbols-outlined text-base">print</span> PDF
            </button>
            <button onClick={attachToJob} title="Export PNG into the job's documents"
              className="h-9 px-2.5 rounded-lg border border-[#C77D3F]/40 text-xs font-semibold text-[#C77D3F] flex items-center gap-1">
              <span className="material-symbols-outlined text-base">attach_file</span> To job docs
            </button>
            {/* Undo / redo (#119) */}
            <button onClick={undo} disabled={!history.canUndo} title="Undo (Ctrl+Z)"
              className="h-9 w-9 rounded-lg border border-gray-200 text-gray-600 flex items-center justify-center disabled:opacity-30">
              <span className="material-symbols-outlined text-base">undo</span>
            </button>
            <button onClick={redo} disabled={!history.canRedo} title="Redo (Ctrl+Shift+Z)"
              className="h-9 w-9 rounded-lg border border-gray-200 text-gray-600 flex items-center justify-center disabled:opacity-30">
              <span className="material-symbols-outlined text-base">redo</span>
            </button>
            <button onClick={save} disabled={saving}
              className={`h-9 px-3.5 rounded-lg text-xs font-bold text-white flex items-center gap-1 disabled:opacity-50 ${dirty ? 'bg-[#C77D3F]' : 'bg-gray-300'}`}>
              <span className="material-symbols-outlined text-base">save</span> {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </button>
          </span>
        </div>

        {/* 3D settings row (#208): per-level wall height + massing roof */}
        {viewMode === '3d' && (
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-100 bg-gray-50 flex-wrap print:hidden">
            {plan.levels.map((l, i) => (
              <span key={i} className="flex items-center gap-1 text-[10px] font-semibold text-gray-500">
                <span className="text-gray-700">{l.name}</span> wall
                <input type="number" min={2} max={20} step={0.5}
                  value={(l.heightIn ?? DEFAULT_WALL_HEIGHT_IN) / 12}
                  onChange={e => {
                    const ft = Number(e.target.value);
                    if (ft >= 2 && ft <= 20) setLevelProp(i, { heightIn: Math.round(ft * 12) });
                  }}
                  className="w-14 border border-gray-200 rounded px-1 py-0.5 bg-white" aria-label={`${l.name} wall height (ft)`} />
                ft · roof
                <select value={l.roof?.kind ?? 'none'}
                  onChange={e => setLevelProp(i, { roof: { kind: e.target.value as RoofSetting['kind'], pitch: l.roof?.pitch ?? 7 } })}
                  className="border border-gray-200 rounded px-1 py-0.5 bg-white" aria-label={`${l.name} roof type`}>
                  <option value="none">none</option>
                  <option value="gable">gable</option>
                  <option value="flat">flat overhang</option>
                </select>
                {l.roof?.kind === 'gable' && (
                  <select value={l.roof.pitch}
                    onChange={e => setLevelProp(i, { roof: { kind: 'gable', pitch: Number(e.target.value) } })}
                    className="border border-gray-200 rounded px-1 py-0.5 bg-white" aria-label={`${l.name} roof pitch`}>
                    {[3, 4, 5, 6, 7, 8, 9, 10, 12].map(p => <option key={p} value={p}>{p}/12</option>)}
                  </select>
                )}
                {i < plan.levels.length - 1 && <span className="mx-1 h-4 w-px bg-gray-200" />}
              </span>
            ))}
            <span className="ml-auto flex items-center gap-2">
              <span className="text-[10px] text-gray-400" title="Kind-based defaults; simple massing roof over the level's wall footprint with a 16 inch overhang">
                Doors to 6'8" · window sill 3'0", 4'0" high · gable = massing slabs over the wall footprint
              </span>
              <button onClick={() => setHelp3d(true)}
                className="h-7 px-2 rounded-full text-[10px] font-semibold border border-gray-200 bg-white text-gray-600 flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">help</span> Help
              </button>
            </span>
          </div>
        )}

        {/* AI-import review banner (#209) — honest about what this is: a draft */}
        {viewMode === '2d' && plan.importMeta && !plan.importMeta.dismissed && (
          <div className="px-3 py-2 border-b border-amber-200 bg-amber-50 print:hidden">
            <div className="flex items-start gap-2 flex-wrap text-xs text-amber-900">
              <span className="material-symbols-outlined text-lg text-amber-600">smart_toy</span>
              <div className="flex-1 min-w-[16rem]">
                <p className="font-bold">
                  AI-extracted draft ({plan.importMeta.confidence} confidence) — check it against the underlay and fix what it misread.
                </p>
                <p>
                  Scale: {plan.importMeta.ref
                    ? <>{plan.importMeta.ref.description} read as <b>{ftIn(plan.importMeta.ref.labelled_in)}</b>{plan.importMeta.ref.text ? ` (printed "${plan.importMeta.ref.text}")` : ''}</>
                    : 'no legible reference — scale assumed'}
                </p>
                {plan.importMeta.notes.length > 0 && (
                  <p className="text-amber-700 mt-0.5">{plan.importMeta.notes.slice(0, 5).join(' · ')}</p>
                )}
              </div>
              {plan.importMeta.ref && (
                <label className="flex items-center gap-1 font-semibold whitespace-nowrap">
                  If that dimension is off, true length
                  <input type="number" min={1} max={500} step={0.5} value={scaleFix} placeholder="ft"
                    onChange={e => setScaleFix(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') applyScaleFix(); }}
                    className="w-16 border border-amber-300 rounded px-1.5 py-1 bg-white" />
                  ft
                  <button onClick={applyScaleFix} disabled={!(parseFloat(scaleFix) > 0)}
                    className="h-7 px-2.5 rounded-lg bg-amber-600 text-white font-bold disabled:opacity-40">
                    Rescale all
                  </button>
                </label>
              )}
              <button onClick={dismissBanner} title="Hide this banner (saved with the plan)"
                className="h-7 px-2 rounded-lg border border-amber-300 text-amber-700 font-semibold">
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Levels + thickness */}
        {viewMode === '2d' && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50 flex-wrap print:hidden">
          {plan.levels.map((l, i) => (
            <button key={i} onClick={() => { setLevelIdx(i); setSelected(null); setDrawStart(null); }}
              className={`h-7 px-2.5 rounded-full text-xs font-semibold border ${i === levelIdx ? 'bg-[#2C1810] text-white border-[#2C1810]' : 'bg-white text-gray-600 border-gray-200'}`}>
              {l.name}
            </button>
          ))}
          <button onClick={() => {
            const n = window.prompt('Level name (e.g. "Second floor", "Foundation")');
            if (n?.trim()) { setPlan(p => ({ ...p, levels: [...p.levels, blankLevel(n.trim())] })); setLevelIdx(plan.levels.length); setDirty(true); }
          }} className="h-7 px-2 rounded-full text-xs font-semibold border border-dashed border-gray-300 text-gray-400">+ level</button>
          <span className="mx-2 h-5 w-px bg-gray-200" />
          {THICKNESSES.map(t => (
            <button key={t.value} onClick={() => setThick(t.value)}
              className={`h-7 px-2 rounded-full text-[10px] font-semibold border ${thick === t.value ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-500 border-gray-200'}`}>
              {t.label}
            </button>
          ))}
          <span className="mx-2 h-5 w-px bg-gray-200" />
          <label className="text-[10px] font-semibold text-gray-500 flex items-center gap-1">
            Snap
            <select value={plan.grid} onChange={e => { setPlan(p => ({ ...p, grid: Number(e.target.value) })); setDirty(true); }}
              className="border border-gray-200 rounded px-1 py-0.5 bg-white">
              <option value={1}>1"</option><option value={3}>3"</option><option value={6}>6"</option><option value={12}>12"</option>
            </select>
          </label>
          {plan.underlay && (
            <>
              <span className="mx-2 h-5 w-px bg-gray-200" />
              <label className="text-[10px] font-semibold text-gray-500 flex items-center gap-1" title="The imported PDF page, drawn under the plan for tracing/correction">
                <button onClick={() => setUnderlay({ visible: plan.underlay!.visible === false })}
                  className={`h-7 w-7 rounded-full border flex items-center justify-center ${plan.underlay.visible !== false ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-400 border-gray-200'}`}
                  aria-label="Toggle underlay">
                  <span className="material-symbols-outlined text-sm">wallpaper</span>
                </button>
                Underlay
                <input type="range" min={0.05} max={1} step={0.05}
                  value={plan.underlay.opacity}
                  onChange={e => setUnderlay({ opacity: Number(e.target.value) })}
                  disabled={plan.underlay.visible === false}
                  className="w-20 accent-[#C77D3F]" aria-label="Underlay opacity" />
              </label>
            </>
          )}
          {tool === 'room' && (
            <>
              <span className="mx-2 h-5 w-px bg-gray-200" />
              <label className="text-[10px] font-semibold text-gray-500 flex items-center gap-1">
                Box size (ft)
                <input type="number" min={1} max={200} placeholder="W" value={roomSize ? roomSize.w / 12 : ''}
                  onChange={e => {
                    const w = Number(e.target.value) * 12;
                    setRoomSize(r => w > 0 ? { w, h: r?.h || w } : null);
                  }}
                  className="w-12 border border-gray-200 rounded px-1 py-0.5 bg-white" />
                ×
                <input type="number" min={1} max={200} placeholder="D" value={roomSize ? roomSize.h / 12 : ''}
                  onChange={e => {
                    const h = Number(e.target.value) * 12;
                    setRoomSize(r => h > 0 ? { w: r?.w || h, h } : null);
                  }}
                  className="w-12 border border-gray-200 rounded px-1 py-0.5 bg-white" />
                {roomSize && (
                  <button onClick={() => setRoomSize(null)} className="text-gray-400 hover:text-gray-700" title="Clear preset — draw corner to corner instead">✕</button>
                )}
              </label>
              {[[12, 16], [16, 20], [24, 24]].map(([w, h]) => (
                <button key={`${w}x${h}`} onClick={() => setRoomSize({ w: w * 12, h: h * 12 })}
                  className={`h-7 px-2 rounded-full text-[10px] font-semibold border ${roomSize?.w === w * 12 && roomSize?.h === h * 12 ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-500 border-gray-200'}`}>
                  {w}×{h}
                </button>
              ))}
            </>
          )}
          <span className="ml-auto text-[10px] text-gray-400">
            {cursor ? `${ftIn(cursor.x)}, ${ftIn(cursor.y)}` : ''}{drawStart && cursor ? ` · run ${ftIn(Math.hypot(cursor.x - drawStart.x, cursor.y - drawStart.y))}` : ''}
          </span>
        </div>
        )}

        <div className="flex-1 min-h-0 flex">
          {/* 3D model + sketch engine (#208) — container stays mounted so the
              engine keeps the sketch/undo state across 2D↔3D switches */}
          <div className={`flex-1 min-w-0 relative bg-[#fbfaf7] ${viewMode === '3d' ? '' : 'hidden'}`}>
            <SketchEngine3D
              active={viewMode === '3d'}
              modelKey={modelKey}
              loadModel={loadModel}
              layers={LAYERS_3D}
              adapter={sketchAdapter}
              adapterKey={planId}
              helpVariant="drafting"
              onError={() => { setMsg('Could not build the 3D model'); setTimeout(() => setMsg(''), 2500); setViewMode('2d'); }}
            />
            {viewMode === '3d' && help3d && (
              <div className="absolute inset-0 z-40 bg-white/95 overflow-y-auto rounded-lg">
                <HelpContent variant="drafting" onDone={() => setHelp3d(false)} />
              </div>
            )}
          </div>
          {viewMode === '2d' && (<>
          {/* Tool rail */}
          <div className="flex flex-col gap-1.5 p-1.5 border-r border-gray-200 bg-white print:hidden">
            {toolBtn('select', 'arrow_selector_tool', 'Select')}
            {toolBtn('wall', 'pen_size_3', 'Wall')}
            {toolBtn('room', 'crop_square', 'Room')}
            {toolBtn('door', 'door_front', 'Door')}
            {toolBtn('window', 'window', 'Window')}
            {toolBtn('dim', 'straighten', 'Dim')}
            {toolBtn('label', 'text_fields', 'Label')}
            <div className="my-0.5 h-px bg-gray-200" title="Site survey tools" />
            {toolBtn('property', 'crop_free', 'Prop.')}
            {toolBtn('fence', 'fence', 'Fence')}
            {toolBtn('tree', 'park', 'Tree')}
            {toolBtn('structure', 'home_work', 'Exist.')}
            <div className="my-0.5 h-px bg-gray-200" />
            {toolBtn('erase', 'ink_eraser', 'Erase')}
            <div className="mt-auto flex flex-col gap-1.5">
              <button onClick={() => setView(v => ({ x: v.x + v.w * 0.075, y: v.y + v.w * 0.075 * (aspect || 0.6), w: v.w * 0.85 }))}
                className="h-10 w-10 rounded-lg bg-white border border-gray-200 text-gray-600 flex items-center justify-center"><span className="material-symbols-outlined">zoom_in</span></button>
              <button onClick={() => setView(v => ({ x: v.x - v.w * 0.088, y: v.y - v.w * 0.088 * (aspect || 0.6), w: v.w * 1.176 }))}
                className="h-10 w-10 rounded-lg bg-white border border-gray-200 text-gray-600 flex items-center justify-center"><span className="material-symbols-outlined">zoom_out</span></button>
            </div>
          </div>

          {/* Canvas */}
          <div className="flex-1 min-w-0 relative bg-[#fbfaf7]">
            <svg
              ref={svgRef}
              viewBox={`${view.x} ${view.y} ${view.w} ${viewH || view.w * 0.6}`}
              className="w-full h-full touch-none select-none"
              style={{ cursor: tool === 'wall' || tool === 'dim' ? 'crosshair' : tool === 'erase' ? 'not-allowed' : 'default' }}
              onPointerDown={e => { if (e.button === 1 || (tool === 'select' && e.shiftKey)) { startPan(e); } else onPointerDown(e); (e.target as Element).setPointerCapture?.(e.pointerId); }}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onWheel={onWheel}
              onDoubleClick={() => { setDrawStart(null); finishPoly(); }}
            >
              {/* imported-PDF tracing underlay (#209) — under everything, its
                  world rect scaled by the same reference as the extraction */}
              {plan.underlay && plan.underlay.visible !== false && (
                <g id="underlay-layer">
                  <image href={plan.underlay.url} x={plan.underlay.x} y={plan.underlay.y}
                    width={plan.underlay.w} height={plan.underlay.h}
                    preserveAspectRatio="none" opacity={plan.underlay.opacity} />
                </g>
              )}
              {/* grid */}
              {gridLines.map((g, i) => g.x !== undefined
                ? <line key={i} x1={g.x} y1={view.y} x2={g.x} y2={view.y + viewH} stroke={g.major ? '#e2dbcf' : '#efeae0'} strokeWidth={view.w / 1200} />
                : <line key={i} x1={view.x} y1={g.y} x2={view.x + view.w} y2={g.y} stroke={g.major ? '#e2dbcf' : '#efeae0'} strokeWidth={view.w / 1200} />)}

              {/* site-survey layer (#95) — under the building elements */}
              <defs>
                <pattern id="hatch" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                  <line x1="0" y1="0" x2="0" y2="8" stroke="#b9b0a2" strokeWidth="1.5" />
                </pattern>
              </defs>
              {level.structures.map(s => {
                const isSel = selected?.kind === 'structure' && selected.id === s.id;
                return (
                  <g key={`st${s.id}`}>
                    <rect x={s.x} y={s.y} width={s.w} height={s.h}
                      fill="url(#hatch)" fillOpacity={0.6}
                      stroke={isSel ? '#C77D3F' : '#8a8377'} strokeWidth={view.w / 500}
                      strokeDasharray={`${view.w / 120} ${view.w / 240}`} />
                    <text x={s.x + s.w / 2} y={s.y + s.h / 2} fontSize={Math.max(6, view.w / 95)}
                      fill={isSel ? '#C77D3F' : '#8a8377'} textAnchor="middle" fontWeight={600}>
                      {s.label}
                    </text>
                  </g>
                );
              })}
              {level.sitePaths.map(sp => {
                const st = SITE_STYLE[sp.kind];
                const isSel = selected?.kind === 'sitepath' && selected.id === sp.id;
                return (
                  <polyline key={`sp${sp.id}`}
                    points={sp.pts.map(p => `${p.x},${p.y}`).join(' ')}
                    fill="none" stroke={isSel ? '#C77D3F' : st.stroke}
                    strokeWidth={sp.kind === 'property' ? view.w / 400 : view.w / 600}
                    strokeDasharray={st.dash} />
                );
              })}
              {level.trees.map(t => {
                const isSel = selected?.kind === 'tree' && selected.id === t.id;
                const c = isSel ? '#C77D3F' : '#5C7F4F';
                return (
                  <g key={`tr${t.id}`}
                    onPointerDown={e => { if (tool === 'select') { e.stopPropagation(); setSelected({ kind: 'tree', id: t.id }); setDragging({ kind: 'tree', id: t.id }); } }}
                    style={tool === 'select' ? { cursor: 'move' } : undefined}>
                    <circle cx={t.x} cy={t.y} r={t.canopy / 2} fill="none" stroke={c}
                      strokeWidth={view.w / 700} strokeDasharray={`${view.w / 200} ${view.w / 350}`} />
                    <circle cx={t.x} cy={t.y} r={Math.max(2, t.canopy / 14)} fill={c} />
                    <line x1={t.x - t.canopy / 7} y1={t.y} x2={t.x + t.canopy / 7} y2={t.y} stroke={c} strokeWidth={view.w / 900} />
                    <line x1={t.x} y1={t.y - t.canopy / 7} x2={t.x} y2={t.y + t.canopy / 7} stroke={c} strokeWidth={view.w / 900} />
                  </g>
                );
              })}

              {/* walls with opening gaps */}
              {level.walls.map(w => {
                const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1;
                const ux = (w.x2 - w.x1) / len, uy = (w.y2 - w.y1) / len;
                const ops = level.openings.filter(o => o.wallId === w.id).sort((a, b) => a.offset - b.offset);
                const segs: { a: number; b: number }[] = [];
                let pos = 0;
                for (const o of ops) { if (o.offset > pos) segs.push({ a: pos, b: o.offset }); pos = o.offset + o.width; }
                if (pos < len) segs.push({ a: pos, b: len });
                const isSel = selected?.kind === 'wall' && selected.id === w.id;
                return (
                  <g key={w.id}>
                    {segs.map((sg, i) => (
                      <line key={i}
                        x1={w.x1 + ux * sg.a} y1={w.y1 + uy * sg.a}
                        x2={w.x1 + ux * sg.b} y2={w.y1 + uy * sg.b}
                        stroke={isSel ? '#C77D3F' : '#2C1810'} strokeWidth={w.thick} strokeLinecap="butt" />
                    ))}
                    {/* opening symbols */}
                    {ops.map(o => {
                      const ax = w.x1 + ux * o.offset, ay = w.y1 + uy * o.offset;
                      const bx = ax + ux * o.width, by = ay + uy * o.width;
                      const nx = -uy, ny = ux;
                      const oSel = selected?.kind === 'opening' && selected.id === o.id;
                      return (
                        <g key={o.id} stroke={oSel ? '#C77D3F' : '#4A6FA5'} fill="none" strokeWidth={view.w / 600}>
                          {o.kind === 'window' && (
                            <>
                              <line x1={ax} y1={ay} x2={bx} y2={by} strokeWidth={w.thick / 4} />
                              <line x1={ax + nx * w.thick / 3} y1={ay + ny * w.thick / 3} x2={bx + nx * w.thick / 3} y2={by + ny * w.thick / 3} strokeWidth={view.w / 900} />
                              <line x1={ax - nx * w.thick / 3} y1={ay - ny * w.thick / 3} x2={bx - nx * w.thick / 3} y2={by - ny * w.thick / 3} strokeWidth={view.w / 900} />
                            </>
                          )}
                          {o.kind === 'door' && (
                            <>
                              <line x1={ax} y1={ay} x2={ax + nx * o.width} y2={ay + ny * o.width} strokeWidth={view.w / 900} />
                              <path d={`M ${ax + nx * o.width} ${ay + ny * o.width} A ${o.width} ${o.width} 0 0 ${ux * ny - uy * nx > 0 ? 0 : 1} ${bx} ${by}`} strokeDasharray={`${view.w / 200} ${view.w / 300}`} strokeWidth={view.w / 1000} />
                            </>
                          )}
                          {/* tag */}
                          <text x={(ax + bx) / 2 + nx * (w.thick + 8)} y={(ay + by) / 2 + ny * (w.thick + 8)}
                            fontSize={Math.max(6, view.w / 90)} fill={oSel ? '#C77D3F' : '#4A6FA5'} stroke="none"
                            textAnchor="middle" fontWeight={700}>{o.tag}</text>
                        </g>
                      );
                    })}
                    {/* endpoint handles when selected */}
                    {isSel && tool === 'select' && (
                      <>
                        <circle cx={w.x1} cy={w.y1} r={Math.max(3, view.w / 120)} fill="#C77D3F"
                          onPointerDown={e => { e.stopPropagation(); setDragging({ kind: 'end1', id: w.id }); }} />
                        <circle cx={w.x2} cy={w.y2} r={Math.max(3, view.w / 120)} fill="#C77D3F"
                          onPointerDown={e => { e.stopPropagation(); setDragging({ kind: 'end2', id: w.id }); }} />
                        <text x={(w.x1 + w.x2) / 2} y={(w.y1 + w.y2) / 2 - w.thick - 4}
                          fontSize={Math.max(6, view.w / 90)} fill="#C77D3F" textAnchor="middle" fontWeight={700}>
                          {ftIn(len)}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}

              {/* dimension lines */}
              {level.dims.map(d => {
                const len = Math.hypot(d.x2 - d.x1, d.y2 - d.y1);
                const isSel = selected?.kind === 'dim' && selected.id === d.id;
                const c = isSel ? '#C77D3F' : '#5C7F4F';
                return (
                  <g key={d.id} stroke={c} strokeWidth={view.w / 1000}>
                    <line x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} />
                    <line x1={d.x1} y1={d.y1 - 4} x2={d.x1} y2={d.y1 + 4} />
                    <line x1={d.x2} y1={d.y2 - 4} x2={d.x2} y2={d.y2 + 4} />
                    <text x={(d.x1 + d.x2) / 2} y={(d.y1 + d.y2) / 2 - 4} fontSize={Math.max(6, view.w / 85)}
                      fill={c} stroke="none" textAnchor="middle" fontWeight={700}>{ftIn(len)}</text>
                  </g>
                );
              })}

              {/* labels */}
              {level.labels.map(lb => (
                <text key={lb.id} x={lb.x} y={lb.y}
                  fontSize={Math.max(7, (lb.size / 12) * view.w / 60)}
                  fill={selected?.kind === 'label' && selected.id === lb.id ? '#C77D3F' : '#2C1810'}
                  textAnchor="middle" fontWeight={600}
                  style={{ cursor: 'move' }}
                  onPointerDown={e => { if (tool === 'select') { e.stopPropagation(); setSelected({ kind: 'label', id: lb.id }); setDragging({ kind: 'label', id: lb.id }); } }}>
                  {lb.text}
                </text>
              ))}

              {/* live preview */}
              <g id="cursor-layer">
                {drawStart && cursor && (tool === 'wall' || tool === 'dim') && (() => {
                  const end = tool === 'wall' ? orthoSnap(drawStart, cursor) : cursor;
                  return (
                    <>
                      <line x1={drawStart.x} y1={drawStart.y} x2={end.x} y2={end.y}
                        stroke="#C77D3F" strokeWidth={tool === 'wall' ? thick : view.w / 800} opacity={0.5} strokeDasharray={`${view.w / 150} ${view.w / 250}`} />
                      <text x={(drawStart.x + end.x) / 2} y={(drawStart.y + end.y) / 2 - 8}
                        fontSize={Math.max(7, view.w / 80)} fill="#C77D3F" textAnchor="middle" fontWeight={700}>
                        {ftIn(Math.hypot(end.x - drawStart.x, end.y - drawStart.y))}
                      </text>
                    </>
                  );
                })()}
                {/* room / structure rectangle preview */}
                {cursor && (tool === 'room' || tool === 'structure') && (drawStart || (tool === 'room' && roomSize)) && (() => {
                  const a = drawStart ?? cursor;
                  const b = tool === 'room' && roomSize && !drawStart
                    ? { x: cursor.x + roomSize.w, y: cursor.y + roomSize.h }
                    : cursor;
                  const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
                  const w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
                  return (
                    <>
                      <rect x={x} y={y} width={w} height={h} fill="none" stroke="#C77D3F" opacity={0.6}
                        strokeWidth={tool === 'room' ? thick : view.w / 500} strokeDasharray={`${view.w / 150} ${view.w / 250}`} />
                      <text x={x + w / 2} y={y - 8} fontSize={Math.max(7, view.w / 80)} fill="#C77D3F" textAnchor="middle" fontWeight={700}>
                        {ftIn(w)} × {ftIn(h)}
                      </text>
                    </>
                  );
                })()}
                {/* polyline preview */}
                {polyPts.length > 0 && (tool === 'property' || tool === 'fence') && (
                  <polyline
                    points={[...polyPts, ...(cursor ? [cursor] : [])].map(p => `${p.x},${p.y}`).join(' ')}
                    fill="none" stroke={SITE_STYLE[tool].stroke} opacity={0.7}
                    strokeWidth={view.w / 500} strokeDasharray={SITE_STYLE[tool].dash} />
                )}
                {/* tree preview */}
                {cursor && tool === 'tree' && (
                  <circle cx={cursor.x} cy={cursor.y} r={60} fill="none" stroke="#5C7F4F" opacity={0.5}
                    strokeWidth={view.w / 700} strokeDasharray={`${view.w / 200} ${view.w / 350}`} />
                )}
                {cursor && tool !== 'select' && (
                  <circle cx={cursor.x} cy={cursor.y} r={Math.max(2, view.w / 300)} fill="#C77D3F" opacity={0.6} />
                )}
              </g>
            </svg>

            {/* hint */}
            <div className="absolute bottom-2 left-2 text-[10px] text-gray-400 bg-white/80 rounded px-2 py-1 print:hidden">
              {tool === 'wall' ? 'Tap to start, tap to place each wall (chains) · double-tap to end · scroll to zoom · shift-drag to pan'
                : tool === 'room' ? (roomSize ? `One tap places a ${roomSize.w / 12}×${roomSize.h / 12} ft wall set (corner = tap point)` : 'Tap two opposite corners to build a 4-wall box — or set a preset size above')
                : tool === 'door' || tool === 'window' ? 'Tap on a wall to place it — edit width/tag with Select'
                : tool === 'dim' ? 'Tap two points to measure'
                : tool === 'property' || tool === 'fence' ? 'Tap each corner along the line · double-tap or Enter to finish · Esc cancels'
                : tool === 'tree' ? 'Tap to place a tree — set canopy size with Select'
                : tool === 'structure' ? 'Tap two opposite corners of the existing building/shed footprint'
                : tool === 'select' ? 'Tap an element · drag orange handles / trees to move · Delete key removes · shift-drag to pan'
                : tool === 'erase' ? 'Tap an element to remove it'
                : 'Tap to place'}
            </div>
          </div>

          {/* Properties panel */}
          {selected && tool === 'select' && (
            <div className="w-52 border-l border-gray-200 bg-white p-2.5 space-y-2 text-xs print:hidden">
              <p className="font-bold text-gray-900 capitalize">{selected.kind}</p>
              {selWall && (
                <>
                  <label className="block">
                    <span className="text-[10px] font-semibold text-gray-500">Thickness</span>
                    <select value={selWall.thick}
                      onChange={e => mutate(l => ({ ...l, walls: l.walls.map(w => w.id === selWall.id ? { ...w, thick: Number(e.target.value) } : w) }))}
                      className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1 bg-white">
                      {THICKNESSES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                  <p className="text-gray-400">Length {ftIn(Math.hypot(selWall.x2 - selWall.x1, selWall.y2 - selWall.y1))}</p>
                </>
              )}
              {selOpening && (
                <>
                  <label className="block">
                    <span className="text-[10px] font-semibold text-gray-500">Type</span>
                    <select value={selOpening.kind}
                      onChange={e => mutate(l => ({ ...l, openings: l.openings.map(o => o.id === selOpening.id ? { ...o, kind: e.target.value as Opening['kind'] } : o) }))}
                      className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1 bg-white">
                      <option value="door">Door</option><option value="window">Window</option><option value="opening">Cased opening</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-semibold text-gray-500">Width (in)</span>
                    <input type="number" value={selOpening.width} min={12} max={192}
                      onChange={e => mutate(l => ({ ...l, openings: l.openings.map(o => o.id === selOpening.id ? { ...o, width: Number(e.target.value) || o.width } : o) }))}
                      className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-semibold text-gray-500">Schedule tag</span>
                    <input value={selOpening.tag}
                      onChange={e => mutate(l => ({ ...l, openings: l.openings.map(o => o.id === selOpening.id ? { ...o, tag: e.target.value } : o) }))}
                      className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1" />
                  </label>
                </>
              )}
              {selLabel && (
                <>
                  <label className="block">
                    <span className="text-[10px] font-semibold text-gray-500">Text</span>
                    <input value={selLabel.text}
                      onChange={e => mutate(l => ({ ...l, labels: l.labels.map(x => x.id === selLabel.id ? { ...x, text: e.target.value } : x) }))}
                      className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-semibold text-gray-500">Size</span>
                    <select value={selLabel.size}
                      onChange={e => mutate(l => ({ ...l, labels: l.labels.map(x => x.id === selLabel.id ? { ...x, size: Number(e.target.value) } : x) }))}
                      className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1 bg-white">
                      <option value={9}>Small</option><option value={12}>Normal</option><option value={18}>Large</option>
                    </select>
                  </label>
                </>
              )}
              {selSitePath && (
                <>
                  <label className="block">
                    <span className="text-[10px] font-semibold text-gray-500">Line type</span>
                    <select value={selSitePath.kind}
                      onChange={e => mutate(l => ({ ...l, sitePaths: l.sitePaths.map(s => s.id === selSitePath.id ? { ...s, kind: e.target.value as SitePath['kind'] } : s) }))}
                      className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1 bg-white">
                      {(Object.keys(SITE_STYLE) as SitePath['kind'][]).map(k => <option key={k} value={k}>{SITE_STYLE[k].label}</option>)}
                    </select>
                  </label>
                  <p className="text-gray-400">
                    {selSitePath.pts.length} points · {ftIn(selSitePath.pts.slice(1).reduce((s, p, i) => s + Math.hypot(p.x - selSitePath.pts[i].x, p.y - selSitePath.pts[i].y), 0))} total
                  </p>
                </>
              )}
              {selTree && (
                <label className="block">
                  <span className="text-[10px] font-semibold text-gray-500">Canopy (ft)</span>
                  <input type="number" min={2} max={80} value={Math.round(selTree.canopy / 12)}
                    onChange={e => mutate(l => ({ ...l, trees: l.trees.map(t => t.id === selTree.id ? { ...t, canopy: Math.max(24, (Number(e.target.value) || 10) * 12) } : t) }))}
                    className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1" />
                </label>
              )}
              {selStructure && (
                <>
                  <label className="block">
                    <span className="text-[10px] font-semibold text-gray-500">Label</span>
                    <input value={selStructure.label}
                      onChange={e => mutate(l => ({ ...l, structures: l.structures.map(s => s.id === selStructure.id ? { ...s, label: e.target.value } : s) }))}
                      className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1" />
                  </label>
                  <div className="flex gap-1.5">
                    <label className="block flex-1">
                      <span className="text-[10px] font-semibold text-gray-500">W (ft)</span>
                      <input type="number" min={1} value={Math.round(selStructure.w / 12)}
                        onChange={e => mutate(l => ({ ...l, structures: l.structures.map(s => s.id === selStructure.id ? { ...s, w: Math.max(12, (Number(e.target.value) || 1) * 12) } : s) }))}
                        className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1" />
                    </label>
                    <label className="block flex-1">
                      <span className="text-[10px] font-semibold text-gray-500">D (ft)</span>
                      <input type="number" min={1} value={Math.round(selStructure.h / 12)}
                        onChange={e => mutate(l => ({ ...l, structures: l.structures.map(s => s.id === selStructure.id ? { ...s, h: Math.max(12, (Number(e.target.value) || 1) * 12) } : s) }))}
                        className="mt-0.5 w-full border border-gray-200 rounded px-1.5 py-1" />
                    </label>
                  </div>
                </>
              )}
              <button
                onClick={deleteSelected}
                className="w-full h-8 rounded-lg border border-red-200 text-red-600 font-bold">
                Delete
              </button>
            </div>
          )}
          </>)}
        </div>
      </div>
    </MainLayout>
  );
}

export const DraftingPage = () => {
  const { id } = useParams<{ id?: string }>();
  return id ? <PlanEditor planId={Number(id)} /> : <PlanList />;
};
